import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  QrCode as QrCodeIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Download as DownloadIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Payment as PaymentIcon,
  HeadsetMic as HeadphonesIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';

const Recharge = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [tab, setTab] = useState(0);

  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [orderStatus, setOrderStatus] = useState('pending');
  const [remainingTime, setRemainingTime] = useState(0);
  const [pollingTimer, setPollingTimer] = useState(null);
  const [creatingOrder, setCreatingOrder] = useState(false);

  // 当前支付方式：'wechat' 或 'alipay'
  const [payMethod, setPayMethod] = useState('wechat');

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 支付方式选择弹窗
  const [payMethodDialogOpen, setPayMethodDialogOpen] = useState(false);
  const [payMethodDialogPkg, setPayMethodDialogPkg] = useState(null);

  // 客服联系方式弹窗
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [serviceWechatId, setServiceWechatId] = useState('');

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // 客服购买按钮点击
  const handleContactService = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/recharge/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.wechat) {
        setServiceWechatId(data.wechat);
      } else {
        setServiceWechatId('暂未设置客服微信号');
      }
    } catch (error) {
      setServiceWechatId('暂未设置客服微信号');
    }
    setServiceDialogOpen(true);
  };

  // 复制微信号
  const copyWechatId = () => {
    navigator.clipboard.writeText(serviceWechatId);
    showMessage('微信号已复制到剪贴板', 'success');
  };

  // 点击充值按钮 → 弹出支付方式选择
  const handlePayMethodSelect = (pkg) => {
    setPayMethodDialogPkg(pkg);
    setPayMethodDialogOpen(true);
  };

  // 确认支付方式 → 创建订单
  const confirmPayMethod = (method) => {
    setPayMethodDialogOpen(false);
    if (payMethodDialogPkg) {
      createPayOrder(payMethodDialogPkg, method);
    }
  };

  const fetchPackages = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/pay-recharge/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setPackages(data.data);
        if (data.data.length > 0 && !selectedPackage) {
          setSelectedPackage(data.data[0]);
        }
      }
    } catch (error) {
      console.error('获取套餐失败:', error);
    }
  }, [selectedPackage]);

  const fetchBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/pay-recharge/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBalance(data.data.balance);
      }
    } catch (error) {
      console.error('获取余额失败:', error);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/pay-recharge/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setOrders(data.data);
      }
    } catch (error) {
      console.error('获取充值记录失败:', error);
    } finally {
      setOrdersLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchPackages(), fetchBalance()]);
      setLoading(false);
    };
    init();

    return () => {
      if (pollingTimer) clearInterval(pollingTimer);
    };
  }, []);

  useEffect(() => {
    if (tab === 1) {
      fetchOrders();
    }
  }, [tab, fetchOrders]);

  const createPayOrder = async (pkg, method = 'wechat') => {
    if (creatingOrder || payDialogOpen) {
      showMessage('正在创建订单，请稍候...', 'warning');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      setCreatingOrder(true);
      setCurrentOrder({ ...pkg, loading: true });
      setPayMethod(method);

      // 根据支付方式选择不同的API
      const apiPath = method === 'alipay'
        ? `${API_BASE_URL}/alipay/create-order`
        : `${API_BASE_URL}/pay-recharge/create-order`;

      const response = await fetch(apiPath, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ packageId: pkg.id })
      });

      const data = await response.json();

      if (data.success) {
        setCreatingOrder(false);
        setCurrentOrder({
          ...pkg,
          orderNo: data.data.orderNo,
          codeUrl: data.data.codeUrl,
          expireTime: data.data.expireTime,
          remainingTime: data.data.remainingTime
        });
        setQrCodeUrl(data.data.codeUrl);
        setOrderStatus('pending');
        setPayDialogOpen(true);
        startPollingOrder(data.data.orderNo, data.data.remainingTime, method);
      } else {
        showMessage(data.message || '创建订单失败', 'error');
        setCreatingOrder(false);
        if (data.message?.includes('体验卡')) {
          setTab(1);
          fetchOrders();
        }
        setCurrentOrder(null);
      }
    } catch (error) {
      console.error('创建订单失败:', error);
      showMessage('创建订单失败，请稍后重试', 'error');
      setCurrentOrder(null);
    } finally {
      setCreatingOrder(false);
    }
  };

  const startPollingOrder = (orderNo, initialRemaining, method = 'wechat') => {
    if (pollingTimer) clearInterval(pollingTimer);

    setRemainingTime(initialRemaining);

    const timer = setInterval(async () => {
      try {
        const token = localStorage.getItem('authToken');
        const apiPath = method === 'alipay'
          ? `${API_BASE_URL}/alipay/query-order?orderNo=${orderNo}`
          : `${API_BASE_URL}/pay-recharge/query-order?orderNo=${orderNo}`;
        const response = await fetch(apiPath, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await response.json();

        if (data.success && data.data) {
          setOrderStatus(data.data.tradeStatus);

          if (data.data.tradeStatus === 'SUCCESS') {
            clearInterval(timer);
            showMessage(`支付成功！获得 ${data.data.amountText} 字`, 'success');
            fetchBalance();
            fetchOrders();
            fetchPackages();
            if (typeof window !== 'undefined') {
              window.localStorage.removeItem('trialRechargeStatus');
              window.localStorage.removeItem('trialRechargeObserved');
              window.dispatchEvent(new CustomEvent('userDataUpdated', {
                detail: { source: 'recharge' }
              }));
            }
            setTimeout(() => {
              setPayDialogOpen(false);
            }, 2000);
          } else if (data.data.tradeStatus === 'CLOSED') {
            clearInterval(timer);
            showMessage('订单已过期，请重新创建', 'warning');
            setPayDialogOpen(false);
          }

          if (data.data.remainingTime) {
            setRemainingTime(data.data.remainingTime);
          }
        }
      } catch (error) {
        console.error('查询订单失败:', error);
      }
    }, 3000);

    setPollingTimer(timer);
  };

  const checkPayment = async () => {
    if (!currentOrder?.orderNo) return;

    try {
      const token = localStorage.getItem('authToken');
      const apiPath = payMethod === 'alipay'
        ? `${API_BASE_URL}/alipay/query-order?orderNo=${currentOrder.orderNo}`
        : `${API_BASE_URL}/pay-recharge/query-order?orderNo=${currentOrder.orderNo}`;
      const response = await fetch(apiPath, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();

      if (data.success && data.data) {
        if (data.data.tradeStatus === 'SUCCESS') {
          showMessage(`支付成功！获得 ${data.data.amountText} 字`, 'success');
          fetchBalance();
          fetchOrders();
          fetchPackages();
          if (pollingTimer) clearInterval(pollingTimer);
          if (typeof window !== 'undefined') {
            window.localStorage.removeItem('trialRechargeStatus');
            window.localStorage.removeItem('trialRechargeObserved');
            window.dispatchEvent(new CustomEvent('userDataUpdated', {
              detail: { source: 'recharge' }
            }));
          }
          setPayDialogOpen(false);
        } else if (data.data.tradeStatus === 'CLOSED') {
          showMessage('订单已过期', 'warning');
          setPayDialogOpen(false);
        } else {
          showMessage('尚未支付成功，请确认已扫码支付', 'info');
        }
      }
    } catch (error) {
      console.error('确认支付失败:', error);
    }
  };

  const refreshQRCode = async () => {
    if (!selectedPackage) return;

    if (currentOrder?.orderNo) {
      try {
        const token = localStorage.getItem('authToken');
        const apiPath = payMethod === 'alipay'
          ? `${API_BASE_URL}/alipay/query-order?orderNo=${currentOrder.orderNo}`
          : `${API_BASE_URL}/pay-recharge/query-order?orderNo=${currentOrder.orderNo}`;
        const response = await fetch(apiPath, { headers: { 'Authorization': `Bearer ${token}` } });
        const data = await response.json();

        if (data.success && data.data) {
          if (data.data.tradeStatus === 'SUCCESS') {
            showMessage('当前订单已支付成功！', 'success');
            if (pollingTimer) clearInterval(pollingTimer);
            setPayDialogOpen(false);
            fetchBalance();
            return;
          }
        }
      } catch (error) {
        console.error('查询旧订单状态失败:', error);
      }
    }

    setCreatingOrder(true);

    if (pollingTimer) {
      clearInterval(pollingTimer);
      setPollingTimer(null);
    }

    if (currentOrder?.orderNo) {
      try {
        const token = localStorage.getItem('authToken');
        const cancelPath = payMethod === 'alipay'
          ? `${API_BASE_URL}/alipay/cancel-order`
          : `${API_BASE_URL}/pay-recharge/cancel-order`;
        await fetch(cancelPath, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ orderNo: currentOrder.orderNo })
        });
      } catch (error) {
        console.error('取消旧订单失败:', error);
      }
    }

    setCurrentOrder(null);
    setQrCodeUrl('');
    setOrderStatus('pending');

    try {
      const token = localStorage.getItem('authToken');
      setCurrentOrder({ ...selectedPackage, loading: true });
      const apiPath = payMethod === 'alipay'
        ? `${API_BASE_URL}/alipay/create-order`
        : `${API_BASE_URL}/pay-recharge/create-order`;

      const response = await fetch(apiPath, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ packageId: selectedPackage.id })
      });

      const data = await response.json();

      if (data.success) {
        setCurrentOrder({
          ...selectedPackage,
          orderNo: data.data.orderNo,
          codeUrl: data.data.codeUrl,
          expireTime: data.data.expireTime,
          remainingTime: data.data.remainingTime
        });
        setQrCodeUrl(data.data.codeUrl);
        setOrderStatus('pending');
        startPollingOrder(data.data.orderNo, data.data.remainingTime, payMethod);
      } else {
        showMessage(data.message || '刷新失败', 'error');
        setCurrentOrder(null);
      }
    } catch (error) {
      console.error('刷新二维码失败:', error);
      showMessage('刷新二维码失败，请稍后重试', 'error');
      setCurrentOrder(null);
    } finally {
      setCreatingOrder(false);
    }
  };

  const saveQRCode = () => {
    if (!qrCodeUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      const link = document.createElement('a');
      const payName = payMethod === 'alipay' ? '支付宝' : '微信';
      link.download = `${payName}支付_${currentOrder?.orderNo || Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      showMessage('二维码已保存到本地', 'success');
    };
    img.onerror = () => {
      fetch(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeUrl)}`)
        .then(res => res.blob())
        .then(blob => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          const payName = payMethod === 'alipay' ? '支付宝' : '微信';
          link.download = `${payName}支付_${currentOrder?.orderNo || Date.now()}.png`;
          link.href = url;
          link.click();
          URL.revokeObjectURL(url);
          showMessage('二维码已保存到本地', 'success');
        })
        .catch(() => {
          showMessage('保存失败，请尝试右键保存图片', 'error');
        });
    };
    img.src = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrCodeUrl)}`;
  };

  const handleClosePayDialog = () => {
    if (pollingTimer) clearInterval(pollingTimer);
    setPayDialogOpen(false);
    setCurrentOrder(null);
    setOrderStatus('pending');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBalance = (num) => {
    if (num >= 10000) {
      return (num / 10000).toFixed(1) + '万';
    }
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#22d3ee' }} />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* 顶部导航栏 - Cyber风格 */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            background: 'rgba(8, 14, 24, 0.9)',
            border: '1px solid rgba(34, 211, 238, 0.2)',
            boxShadow: '0 0 20px rgba(34, 211, 238, 0.1)',
          }}>
            <Button
              variant="text"
              onClick={() => navigate(-1)}
              startIcon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
              sx={{
                color: 'rgba(224, 242, 254, 0.7)',
                '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34, 211, 238, 0.1)' },
                fontSize: '0.8rem',
              }}
            >
              返回
            </Button>

            <Typography className="workspace-panel-title">
              充值中心
            </Typography>

            {/* 余额显示 */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '8px',
              background: 'rgba(251, 191, 36, 0.08)',
              border: '1px solid rgba(251, 191, 36, 0.25)',
            }}>
              <WalletIcon sx={{ fontSize: 14, color: '#fbbf24' }} />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#fbbf24' }}>
                {formatBalance(balance)}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(251, 191, 36, 0.7)' }}>字</Typography>
            </Box>
          </Box>
        </motion.div>

        {/* 主内容区 - Cyber卡片 */}
        <Paper className="cyber-card" sx={{ p: 0, overflow: 'hidden' }}>
          {/* 标签页 */}
          <Tabs
            value={tab}
            onChange={(e, v) => setTab(v)}
            sx={{
              px: 2,
              borderBottom: '1px solid rgba(34, 211, 238, 0.15)',
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                fontSize: '0.8rem',
                color: 'rgba(224, 242, 254, 0.6)',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.3px',
              },
              '& .Mui-selected': {
                color: '#22d3ee',
                fontWeight: 600,
              },
            }}
            textColor="inherit"
          >
            <Tab
              icon={<QrCodeIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="扫码充值"
            />
            <Tab
              icon={<HistoryIcon sx={{ fontSize: 15 }} />}
              iconPosition="start"
              label="充值记录"
            />
          </Tabs>

          <Box sx={{ p: 2 }}>
            <AnimatePresence mode="wait">
              {tab === 0 && (
                <motion.div
                  key="recharge"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  transition={{ duration: 0.25 }}
                >
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(224, 242, 254, 0.5)', mb: 1.5 }}>
                    选择充值套餐，微信/支付宝扫码支付，立即到账
                  </Typography>

                  <Grid container spacing={2}>
                    {packages.map((pkg, index) => (
                      <Grid item xs={12} sm={6} md={4} key={pkg.id} sx={{ display: 'flex' }}>
                        <motion.div
                          style={{ width: '100%', display: 'flex' }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.06, duration: 0.3 }}
                        >
                          <Card
                            onClick={() => setSelectedPackage(pkg)}
                            className={selectedPackage?.id === pkg.id ? 'cyber-card glow-cyan' : 'cyber-card'}
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              border: selectedPackage?.id === pkg.id
                                ? '1px solid rgba(34, 211, 238, 0.5)'
                                : '1px solid rgba(34, 211, 238, 0.15)',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                border: '1px solid rgba(34, 211, 238, 0.4)',
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                              {/* 标题行 */}
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography sx={{
                                  fontSize: '1rem',
                                  fontWeight: 700,
                                  background: 'linear-gradient(135deg, #22d3ee 0%, #a78bfa 100%)',
                                  WebkitBackgroundClip: 'text',
                                  WebkitTextFillColor: 'transparent',
                                  letterSpacing: '0.5px',
                                }}>
                                  {pkg.name}
                                </Typography>
                                {(pkg.discount || pkg.isExperience) && (
                                  <Chip
                                    label={pkg.isExperience ? '首发特惠' : `${pkg.discount}折`}
                                    size="small"
                                    sx={{
                                      height: 22,
                                      fontSize: '0.7rem',
                                      fontWeight: 700,
                                      bgcolor: pkg.isExperience
                                        ? 'rgba(34, 211, 238, 0.18)'
                                        : 'rgba(239, 68, 68, 0.18)',
                                      color: pkg.isExperience ? '#22d3ee' : '#ef4444',
                                      border: `1px solid ${pkg.isExperience ? 'rgba(34, 211, 238, 0.35)' : 'rgba(239, 68, 68, 0.35)'}`,
                                      '& .MuiChip-label': { px: 0.75 }
                                    }}
                                  />
                                )}
                              </Box>

                              {/* 价格区域 */}
                              <Box sx={{ mb: 1 }}>
                                {/* 原价行（有折扣才显示，否则占位保持高度） */}
                                <Typography sx={{
                                  fontSize: '0.75rem',
                                  color: pkg.originalPrice && pkg.originalPrice > pkg.price
                                    ? 'rgba(224, 242, 254, 0.4)'
                                    : 'transparent',
                                  textDecoration: pkg.originalPrice && pkg.originalPrice > pkg.price
                                    ? 'line-through'
                                    : 'none',
                                  mb: 0.25,
                                  minHeight: '1.2em',
                                  display: 'block',
                                }}>
                                  {pkg.originalPrice && pkg.originalPrice > pkg.price
                                    ? `原价 ${pkg.originalPriceText}`
                                    : '\u00A0'}
                                </Typography>

                                {/* 现价 + 单位 */}
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                  <Typography sx={{
                                    fontSize: '1.5rem',
                                    fontWeight: 800,
                                    color: pkg.isExperience ? '#fbbf24' : '#22d3ee',
                                    textShadow: pkg.isExperience
                                      ? '0 0 12px rgba(251, 191, 36, 0.5)'
                                      : '0 0 12px rgba(34, 211, 238, 0.5)',
                                  }}>
                                    {pkg.priceText}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.8rem', color: 'rgba(224, 242, 254, 0.5)' }}>
                                    / {pkg.amountText}字
                                  </Typography>
                                </Box>

                                {/* 节省行（有折扣才显示，否则占位保持高度） */}
                                <Typography sx={{
                                  fontSize: '0.7rem',
                                  color: '#4ade80',
                                  fontWeight: 600,
                                  mt: 0.25,
                                  minHeight: '1.2em',
                                  display: 'block',
                                }}>
                                  {pkg.originalPrice && pkg.originalPrice > pkg.price
                                    ? `节省 ¥${((pkg.originalPrice - pkg.price) / 100).toFixed(2)}`
                                    : '\u00A0'}
                                </Typography>
                              </Box>

                              {/* 描述（始终显示，空则占位） */}
                              <Typography sx={{
                                fontSize: '0.75rem',
                                color: 'rgba(224, 242, 254, 0.5)',
                                lineHeight: 1.4,
                                minHeight: '1.4em',
                              }}>
                                {pkg.description || '\u00A0'}
                              </Typography>
                            </CardContent>

                            {/* 按钮行 */}
                            <Box sx={{ p: 1.5, pt: 0 }}>
                              <Button
                                variant="outlined"
                                fullWidth
                                size="small"
                                disabled={creatingOrder || pkg.purchased}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!pkg.purchased) {
                                    // 先选中套餐，再弹出支付方式选择
                                    setSelectedPackage(pkg);
                                    setPayMethod('wechat');
                                    handlePayMethodSelect(pkg);
                                  }
                                }}
                                className={!pkg.purchased ? 'workspace-btn-neon-cyan' : ''}
                                sx={{
                                  fontSize: '0.75rem',
                                  py: 0.75,
                                  fontWeight: 600,
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {pkg.purchased ? '已购买' : '立即充值'}
                              </Button>
                            </Box>
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {packages.length === 0 && (
                    <Box textAlign="center" py={5}>
                      <Typography sx={{ color: 'rgba(224, 242, 254, 0.5)', fontSize: '0.85rem' }}>
                        暂无可用套餐，请联系管理员
                      </Typography>
                    </Box>
                  )}
                </motion.div>
              )}

              {tab === 1 && (
                <motion.div
                  key="history"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.25 }}
                >
                  {ordersLoading ? (
                    <Box textAlign="center" py={5}>
                      <CircularProgress size={24} sx={{ color: '#22d3ee' }} />
                    </Box>
                  ) : orders.length > 0 ? (
                    <Box>
                      {orders.map((order, index) => (
                        <motion.div
                          key={order.orderNo}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.04 }}
                        >
                          <Box
                            className="cyber-card"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              px: 1.5,
                              py: 1.25,
                              mb: 1,
                              transition: 'all 0.2s',
                              '&:hover': {
                                borderColor: 'rgba(34, 211, 238, 0.35)',
                              },
                            }}
                          >
                            <Box>
                              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(224, 242, 254, 0.95)', fontWeight: 500 }}>
                                {order.packageName || '充值'}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(224, 242, 254, 0.45)', mt: 0.25 }}>
                                {new Date(order.paidAt || order.createdAt).toLocaleString('zh-CN')}
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                              <Typography sx={{ fontSize: '0.9rem', color: '#22d3ee', fontWeight: 600 }}>
                                +{order.amountText}
                              </Typography>
                              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(224, 242, 254, 0.45)' }}>
                                {order.priceText}
                              </Typography>
                            </Box>
                            <Chip
                              label={order.tradeStatus === 'SUCCESS' ? '成功' : order.tradeStatusDesc}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                bgcolor: order.tradeStatus === 'SUCCESS' ? 'rgba(34, 211, 238, 0.12)' : 'rgba(224, 242, 254, 0.08)',
                                color: order.tradeStatus === 'SUCCESS' ? '#22d3ee' : 'rgba(224, 242, 254, 0.5)',
                              }}
                            />
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  ) : (
                    <Box textAlign="center" py={5}>
                      <HistoryIcon sx={{ fontSize: 36, color: 'rgba(224, 242, 254, 0.3)', mb: 1 }} />
                      <Typography sx={{ color: 'rgba(224, 242, 254, 0.5)', fontSize: '0.85rem' }}>
                        暂无充值记录
                      </Typography>
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
        </Paper>

        {/* 支付二维码对话框 */}
        <Dialog
          open={payDialogOpen}
          onClose={handleClosePayDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(8, 14, 24, 0.98)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 211, 238, 0.25)',
              boxShadow: '0 0 40px rgba(34, 211, 238, 0.15)',
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(34, 211, 238, 0.15)',
            pb: 1.5,
            px: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {payMethod === 'alipay' ? (
                <PaymentIcon sx={{ fontSize: 16, color: '#1677ff' }} />
              ) : (
                <QrCodeIcon sx={{ fontSize: 16, color: '#22d3ee' }} />
              )}
              <Typography className="workspace-panel-title" sx={{ fontSize: '0.9rem !important' }}>
                {payMethod === 'alipay' ? '支付宝支付' : '微信支付'}
              </Typography>
            </Box>
            <IconButton onClick={handleClosePayDialog} size="small" sx={{ color: 'rgba(224, 242, 254, 0.5)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 2, px: 2 }}>
            {currentOrder && (
              <Box textAlign="center">
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(224, 242, 254, 0.5)' }}>
                    支付金额
                  </Typography>
                  <Typography
                    className="neon-text-cyan"
                    sx={{
                      fontSize: '1.4rem',
                      fontWeight: 700,
                    }}
                  >
                    {currentOrder.priceText}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(224, 242, 254, 0.5)' }}>
                    {currentOrder.amountText}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    bgcolor: '#fff',
                    borderRadius: '10px',
                    p: 1.5,
                    display: 'inline-block',
                    mb: 1.5,
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                  }}
                >
                  {orderStatus !== 'SUCCESS' && qrCodeUrl ? (
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(qrCodeUrl)}`}
                      alt="支付二维码"
                      style={{ width: 180, height: 180, display: 'block', borderRadius: 6 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 180,
                        height: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(34, 211, 238, 0.08)',
                        borderRadius: '6px',
                      }}
                    >
                      {orderStatus === 'SUCCESS' ? (
                        <CheckIcon sx={{ fontSize: 56, color: '#22d3ee' }} />
                      ) : (
                        <CircularProgress size={32} sx={{ color: '#22d3ee' }} />
                      )}
                    </Box>
                  )}
                </Box>


                <Box>
                  {orderStatus === 'SUCCESS' ? (
                    <Typography className="neon-text-cyan" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                      支付成功！正在处理...
                    </Typography>
                  ) : orderStatus === 'CLOSED' ? (
                    <Typography sx={{ color: '#ef4444', fontSize: '0.85rem' }}>
                      订单已过期
                    </Typography>
                  ) : (
                    <>
                      <Typography sx={{ color: 'rgba(224, 242, 254, 0.6)', fontSize: '0.75rem' }}>
                        {payMethod === 'alipay'
                          ? '请使用支付宝扫描上方二维码支付'
                          : '请使用微信扫描上方二维码支付'}
                      </Typography>
                      <Typography sx={{ color: 'rgba(224, 242, 254, 0.4)', fontSize: '0.7rem', mt: 0.5 }}>
                        剩余时间: {formatTime(remainingTime)}
                      </Typography>
                    </>
                  )}
                </Box>

                <Box display="flex" gap={1} justifyContent="center" mt={2}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                    onClick={refreshQRCode}
                    className="workspace-btn-neon-cyan"
                    sx={{ minWidth: 110 }}
                  >
                    刷新
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                    onClick={checkPayment}
                    className="workspace-btn-neon-cyan"
                    sx={{ minWidth: 110 }}
                  >
                    我已支付
                  </Button>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* 支付方式选择对话框 */}
        <Dialog
          open={payMethodDialogOpen}
          onClose={() => setPayMethodDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(8, 14, 24, 0.98)',
              borderRadius: '12px',
              border: '1px solid rgba(34, 211, 238, 0.25)',
              boxShadow: '0 0 40px rgba(34, 211, 238, 0.15)',
            }
          }}
        >
          <DialogTitle sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid rgba(34, 211, 238, 0.15)',
            pb: 1.5,
            px: 2,
          }}>
            <Typography className="workspace-panel-title" sx={{ fontSize: '0.9rem !important' }}>
              选择支付方式
            </Typography>
            <IconButton onClick={() => setPayMethodDialogOpen(false)} size="small" sx={{ color: 'rgba(224, 242, 254, 0.5)' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 2, px: 2 }}>
            {payMethodDialogPkg && (
              <Box textAlign="center">
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(224, 242, 254, 0.5)', mb: 2 }}>
                  {payMethodDialogPkg.name} - {payMethodDialogPkg.priceText}
                </Typography>

                {/* 微信支付 */}
                <Box
                  onClick={() => confirmPayMethod('wechat')}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: '10px',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                    bgcolor: 'rgba(34, 211, 238, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      border: '1px solid rgba(34, 211, 238, 0.5)',
                      bgcolor: 'rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: '#07c160',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>微</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(224, 242, 254, 0.95)' }}>
                        微信支付
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'rgba(224, 242, 254, 0.45)' }}>
                        手机用户可用另一台设备扫码支付
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* 支付宝支付 */}
                <Box
                  onClick={() => confirmPayMethod('alipay')}
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid rgba(34, 211, 238, 0.2)',
                    bgcolor: 'rgba(34, 211, 238, 0.05)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      border: '1px solid rgba(34, 211, 238, 0.5)',
                      bgcolor: 'rgba(34, 211, 238, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '8px',
                      bgcolor: '#1677ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem' }}>支</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left', flex: 1 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(224, 242, 254, 0.95)' }}>
                        支付宝
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#4ade80', fontWeight: 500 }}>
                        支持从相册选择二维码扫码支付
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Typography sx={{ fontSize: '0.65rem', color: 'rgba(224, 242, 254, 0.35)', mt: 2 }}>
                  点击任意支付方式后将生成对应二维码
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* 客服联系方式弹窗 */}
        <Dialog
          open={serviceDialogOpen}
          onClose={() => setServiceDialogOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgba(15, 23, 42, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(34, 211, 238, 0.3)',
              borderRadius: '12px',
            }
          }}
        >
          <DialogTitle sx={{
            color: '#22d3ee',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            textAlign: 'center',
            pb: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <HeadphonesIcon sx={{ fontSize: 20 }} />
            联系客服购买卡号
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', pt: 1 }}>
            <Typography sx={{ color: 'rgba(224, 242, 254, 0.7)', fontSize: '0.85rem', mb: 2 }}>
              请添加客服微信，咨询购买卡号
            </Typography>
            <Box
              sx={{
                p: 3,
                borderRadius: '10px',
                bgcolor: 'rgba(34, 211, 238, 0.08)',
                border: '1px solid rgba(34, 211, 238, 0.2)',
                mb: 2
              }}
            >
              <Typography sx={{ color: 'rgba(224, 242, 254, 0.5)', fontSize: '0.7rem', mb: 1 }}>
                客服微信号
              </Typography>
              <Typography sx={{
                color: '#22d3ee',
                fontSize: '1.3rem',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                letterSpacing: '2px'
              }}>
                {serviceWechatId}
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={copyWechatId}
              sx={{
                bgcolor: 'rgba(34, 211, 238, 0.15)',
                color: '#22d3ee',
                border: '1px solid rgba(34, 211, 238, 0.4)',
                fontWeight: 'bold',
                py: 1.2,
                '&:hover': {
                  bgcolor: 'rgba(34, 211, 238, 0.25)',
                }
              }}
            >
              复制微信号
            </Button>
            <Typography sx={{ color: 'rgba(224, 242, 254, 0.4)', fontSize: '0.7rem', mt: 2 }}>
              复制后打开微信搜索添加
            </Typography>
          </DialogContent>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={handleCloseSnackbar}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </motion.div>
  );
};

export default Recharge;
